<template>
  <div class="flex gap-2 pt-px">

    <client-only>
      <div class="flex sm:flex-1 gap-2 items-center">
        <transition-group name="f-fade">
          <template v-if="!albumAddedSinceMode && !isSearchMode">
            <div v-if="hasFilterButton" key="filters-popover" class="inline-flex">
              <u-popover
                mode="hover"
                :disabled="!areFiltersApplied || !screenMd"
                :open-delay="180"
                :ui="{wrapper: 'flex', width: 'max-w-sm', base: 'p-3'}"
                :popper="{placement: 'bottom-start'}"
              >
                <core-responsive-button
                  icon="i-ri-equalizer-line"
                  :variant="areFiltersApplied ? 'solid': 'outline'"
                  :color="areFiltersApplied ? 'heroblue': 'charcoal'"
                  @click="toggle"
                >Filters <span class="text-xs bg-white/30 rounded px-1 py-0.5 leading-none" v-if="filterCount">{{filterCount}}</span></core-responsive-button>
                <template #panel>
                  <div class="space-y-2 min-w-[220px]">
                    <p class="font-semibold leading-none">Filters</p>
                    <p class="text-xs text-charcoal-400">{{filterCount}} applied</p>
                    <div v-if="appliedFilterBadges.length" class="flex flex-wrap gap-1.5">
                      <u-badge
                        v-for="badge in appliedFilterBadges"
                        :key="badge.key"
                        :color="badge.color"
                        size="xs"
                        :label="badge.label"
                        :ui="{base: 'max-w-[220px] truncate'}"
                      />
                    </div>
                    <p v-else class="text-xs text-charcoal-400">No filters applied.</p>
                  </div>
                </template>
              </u-popover>
            </div>

            <core-responsive-button
              v-if="areFiltersApplied"
              :icon="COMMON_ICONS.close"
              trailing
              variant="outline"
              color="charcoal"
              @click="clearFilters"
              label="Clear Filters"
            >Clear Filters</core-responsive-button>
            <span class="hidden md:inline text-sm text-gray-500 italic" v-if="areFiltersApplied && !isLoadingFiles">{{filesCountFallback}} Results</span>
          </template>
          <div v-else-if="isSearchMode" class="items-center gap-3 hidden md:flex">
            <u-button
              :icon="COMMON_ICONS.close"
              variant="outline"
              color="charcoal"
              @click="search"
              label="Clear Search"
            />
            <p class="font-semibold">Your search for "{{searchTerm}}" returned {{files?.length ? '' : 'no'}} results.</p>
          </div>
          <template v-else>
            <template v-if="albumAddedSinceMode">
              <u-button color="charcoal"  @click="viewFullAlbum" label="View Full Album" />
            </template>
          </template>
        </transition-group>
      </div>

      <div class="flex flex-1 sm:flex-0 items-center justify-end gap-2">
        <template v-if="!albumAddedSinceMode">
          <core-search-input
            v-if="!collectionDescriptor?.contextType"
            :model-value="searchTerm"
            placeholder="Name & Description"
            @search="search"
            @clear="search"
            :loading="isSearching"
          />
        </template>

        <slot name="custom-actions" />

        <div class="flex gap-1.5">
          <file-list-grid-size />
        </div>
      </div>

      <u-slideover v-model="showFilterPanel" side="left" :ui="filterPanelConfig">
        <file-filter-panel
          :files-store-id="filesStoreId"
          :context-type="contextType"
          :context="context"
          :allowed-file-types="allowedFileTypes"
          :hide-date-filter="hideDateFilter"
          @toggle="toggle"
        />
      </u-slideover>

    </client-only>
  </div>
</template>

<script setup>
  import {storeToRefs} from 'pinia';
  import {makeFilesStore} from "~/stores/files.js";
  import {useScreenSize} from '~/composables/core/screen-size.js';

  const props = defineProps({
    isInPicker: Boolean,
    filesStoreId: String,
    contextType: String,
    context: Object,
    hasFilterButton: {
      type: Boolean,
      default: true
    },
    hideDateFilter: Boolean,
    // Restricts the filter panel's Media Type options - forwarded straight through to
    // file-filter-panel's own allowedFileTypes (undefined = all types). With 1 or fewer types the
    // panel removes the Media Type section and applies the type implicitly, so file types are then
    // excluded from areFiltersApplied/filterCount/appliedFilterBadges below - they aren't something
    // the customer "applied". The list itself is restricted by the file list's scopeFileTypes.
    allowedFileTypes: {
      type: Array,
      default: undefined
    }
  });

  const storeFiltersState = inject('storeFiltersState', false);

  const filesStore = props.filesStoreId
    ? makeFilesStore(props.filesStoreId)()
    : useFilesStore();

  const {collectionDescriptor, filesCount, filesCountFallback, files, isLoadingFiles} = storeToRefs(filesStore);
  const {md: screenMd} = useScreenSize();
  const fuzzyDate = useFuzzyDate();

  // The store's own areFiltersApplied/filterCount getters count EVERY fileType - so a photo-only
  // picker would permanently show "1 filter applied" and a removable "Photo" badge for something
  // the customer never chose. Mirrors filter-panel.vue's own effectiveAreFiltersApplied/
  // effectiveFilterCount, which has the same problem for the same reason.
  const isFileTypeSelectable = computed(() => !props.allowedFileTypes || props.allowedFileTypes.length > 1);

  const activeDescriptorFileTypes = computed(() =>
    isFileTypeSelectable.value ? (collectionDescriptor.value?.fileTypes || []) : []
  );

  const areFiltersApplied = computed(() => Boolean(
    collectionDescriptor.value?.albums?.length ||
    collectionDescriptor.value?.tags?.length ||
    collectionDescriptor.value?.people?.length ||
    activeDescriptorFileTypes.value.length ||
    collectionDescriptor.value?.organization?.length ||
    collectionDescriptor.value?.dateRange?.val
  ));

  const filterCount = computed(() => {
    let count = 0;

    if (collectionDescriptor.value?.albums?.length) {
      count += collectionDescriptor.value.albums.length;
    }

    if (collectionDescriptor.value?.tags?.length) {
      count += collectionDescriptor.value.tags.length;
    }

    if (collectionDescriptor.value?.people?.length) {
      count += collectionDescriptor.value.people.length;
    }

    count += activeDescriptorFileTypes.value.length;

    if (collectionDescriptor.value?.organization?.length) {
      count += collectionDescriptor.value.organization.length;
    }

    if (collectionDescriptor.value?.dateRange?.val) {
      count += 1;
    }

    return count;
  });

  const fileTypeBadgeLabels = {
    photo: 'Photo',
    video: 'Video',
    audio: 'Audio',
    document: 'Document',
    project: 'Project'
  };

  const organizationBadgeLabels = {
    favorites: 'Favorites',
    not_in_album: 'Not In Any Album',
    not_tagged: 'Untagged'
  };

  const badgeColors = {
    fileType: 'teal',
    organization: 'violet',
    dateRange: 'amber',
    people: 'orange',
    albums: 'blue',
    tags: 'emerald'
  };

  function getDateRangeLabel(dateRange) {
    if (!dateRange?.val) {
      return '';
    }

    return dateRange.displayText || fuzzyDate.getFormattedString({date: dateRange.val[0], type: dateRange.type});
  }

  const appliedFilterBadges = computed(() => {
    const descriptor = collectionDescriptor.value || {};
    const badges = [];
    const fileTypes = activeDescriptorFileTypes.value;
    const organization = Array.isArray(descriptor.organization) ? descriptor.organization : [];
    const people = Array.isArray(descriptor.people) ? descriptor.people : [];
    const albums = Array.isArray(descriptor.albums) ? descriptor.albums : [];
    const tags = Array.isArray(descriptor.tags) ? descriptor.tags : [];

    fileTypes.forEach(type => {
      badges.push({
        key: `fileType-${type}`,
        label: fileTypeBadgeLabels[type] || type,
        color: badgeColors.fileType
      });
    });

    organization.forEach(filter => {
      badges.push({
        key: `organization-${filter}`,
        label: organizationBadgeLabels[filter] || filter,
        color: badgeColors.organization
      });
    });

    const dateRangeLabel = getDateRangeLabel(descriptor.dateRange);

    if (dateRangeLabel) {
      badges.push({
        key: 'date-range',
        label: dateRangeLabel,
        color: badgeColors.dateRange
      });
    }

    people.forEach(person => {
      if (person?.name) {
        badges.push({
          key: `person-${person.id || person.name}`,
          label: person.name,
          color: badgeColors.people
        });
      }
    });

    albums.forEach(album => {
      if (album?.name) {
        badges.push({
          key: `album-${album.id || album.name}`,
          label: album.name,
          color: badgeColors.albums
        });
      }
    });

    tags.forEach(tag => {
      if (tag?.name) {
        badges.push({
          key: `tag-${tag.id || tag.name}`,
          label: tag.name,
          color: badgeColors.tags
        });
      }
    });

    return badges;
  });

  const albumAddedSinceMode = computed(() => Boolean(collectionDescriptor.value?.addedSince));

  const searchTerm = ref('');
  const isSearching = ref(false);
  const isSearchMode = computed(() => Boolean(searchTerm.value));

  watch(() => collectionDescriptor.value?.search, newVal => (searchTerm.value = newVal || ''), {immediate: true});

  async function search({q = ''} = {}) {
    try {
      isSearching.value = true;
      searchTerm.value = q;

      await filesStore.getFiles({
        ...collectionDescriptor.value,
        search: searchTerm.value
      });
    } catch (e) {
      useErrorToast().add();
    } finally {
      isSearching.value = false;
    }
  }

  // the store's clearFilters() only resets the customer's filters - scopeFileTypes survives it,
  // so a type-scoped list stays scoped.
  function clearFilters() {
    filesStore.clearFilters({storeCollection: storeFiltersState});
  }

  const showFilterPanel = ref(false);
  function toggle() {
    showFilterPanel.value = !showFilterPanel.value;
  }

  const router = useRouter();
  function viewFullAlbum() {
    router.push({
      query: {
        since: undefined
      }
    });
  }

  watch(() => showFilterPanel.value, newVal => {
    const eventBus = props.isInPicker ? 'drawer-adjust-picker' : 'drawer-adjust-main';
    useEventBus(eventBus).emit({
      width: newVal ? 410 : 0
    });
  });
  const filterPanelConfig = {
    overlay: {
      base: 'fixed inset-0 transition-opacity',
      background: 'bg-black/25'
    },
    width: `max-w-[410px]`
  }

</script>
